import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import ErrorBoundary from "@/components/layout/components/ErrorBoundary";
import { FilePreviewContent, getInlineClassName } from "./FilePreviewContent";
import { FilePreviewErrorState } from "./FilePreviewErrorState";

interface FilePreviewInlineProps {
  fileUrl: string;
  fileName?: string;
  mimeType?: string;
  maxWidth?: number;
  maxHeight?: number;
  fullPage?: boolean;
}

export const FilePreviewInline = memo(
  ({
    fileUrl,
    fileName,
    mimeType,
    maxWidth,
    maxHeight,
    fullPage = false,
  }: FilePreviewInlineProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();

    const handleError = useCallback(
      (error: Error) => {
        toast({
          title: t("filePreview.errorLoadingPdf"),
          description: error.message || t("filePreview.renderFileError"),
          variant: "destructive",
        });
      },
      [toast, t],
    );

    const errorFallback = useMemo(
      () => (
        <FilePreviewErrorState
          title={t("filePreview.errorLoadingPdf")}
          message={t("filePreview.renderFileError")}
          isFullScreen={fullPage}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
        />
      ),
      [t, fullPage, maxWidth, maxHeight],
    );

    return (
      <ErrorBoundary onError={handleError} fallback={errorFallback}>
        <div className={getInlineClassName(fullPage)}>
          <FilePreviewContent
            fileUrl={fileUrl}
            fileName={fileName}
            mimeType={mimeType}
            maxWidth={maxWidth}
            maxHeight={maxHeight}
            isFullScreen={fullPage}
            isScrollLayout={fullPage}
          />
        </div>
      </ErrorBoundary>
    );
  },
);
